import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FilePenLine, Search, Users2, Plus, Trash2, ToggleRight, Download, X } from "lucide-react";
import constant from "../../constant";
import FileSaver from 'file-saver';
import Papa from 'papaparse';

const HospitalsTable = ({ hospitals, onDelete, onEdit }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [localHospitals, setLocalHospitals] = useState(hospitals);
  const [selectedHospitals, setSelectedHospitals] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({ totalReceivable: 0, totalPayable: 0 });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // State for delete confirmation modal
  const [hospitalToDelete, setHospitalToDelete] = useState(null); // Track hospital to delete

  const userRole = localStorage.getItem("role");

  useEffect(() => {
    setLocalHospitals(hospitals);
  }, [hospitals]);

  // useEffect(() => {
  //   const fetchDashboardStats = async () => {
  //     try {
  //       const token = localStorage.getItem("token");
  //       if (!token) {
  //         console.error("No token found");
  //         return;
  //       }

  //       const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/dashboard`, {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       });

  //       if (response.ok) {
  //         const result = await response.json();
  //         setDashboardStats(result.data);
  //       } else {
  //         const errorResult = await response.json();
  //         console.error("Failed to fetch dashboard stats:", errorResult);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching dashboard stats:", error);
  //     }
  //   };

  //   fetchDashboardStats();
  // }, []);

  const handleToggleStatus = async (hospitalId, currentStatus) => {
    setLocalHospitals((prevHospitals) =>
      prevHospitals.map((hospital) =>
        hospital._id === hospitalId ? { ...hospital, isActive: !currentStatus } : hospital
      )
    );

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found");
        return;
      }

      const response = await fetch(
        `${constant.REACT_APP_API_URL}/hospital/${hospitalId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isActive: !currentStatus }),
        }
      );

      if (response.ok) {
        onEdit({ _id: hospitalId, isActive: !currentStatus });
      } else {
        setLocalHospitals((prevHospitals) =>
          prevHospitals.map((hospital) =>
            hospital._id === hospitalId ? { ...hospital, isActive: currentStatus } : hospital
          )
        );
        const result = await response.json();
        throw new Error(result.message || "Failed to toggle hospital status");
      }
    } catch (error) {
      console.error(error);
      alert(error.message);
    }
  };

  const handleSelectAll = () => {
    if (selectedHospitals.length === filteredHospitals.length) {
      setSelectedHospitals([]);
    } else {
      setSelectedHospitals(filteredHospitals.map((hospital) => hospital._id));
    }
  };

  const handleRowSelect = (hospitalId) => {
    setSelectedHospitals((prevSelected) =>
      prevSelected.includes(hospitalId)
        ? prevSelected.filter((id) => id !== hospitalId)
        : [...prevSelected, hospitalId]
    );
  };

  const filteredHospitals = localHospitals.filter((hospital) =>
    hospital.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    hospital.mobile.includes(searchQuery) ||
    hospital.shippingAddress.city.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const convertJsonToCsv = (jsonData) => {
    const csv = Papa.unparse({
      fields: ["name", "hospitalType", "mobile", "gstNo", "billingAddress", "shippingAddress", "createdAt"],
      data: jsonData.map(item => ({
        name: item.name,
        hospitalType: item.hospitalType || 'N/A',
        mobile: item.mobile || 'N/A',
        gstNo: item.gstNo || 'N/A',
        billingAddress: `${item.name || item.name}, ${item?.streetAddress || item.street}, ${item.city || item.city}, ${item.state || item.state}, ${item.country || 'Unknown'}, ${item.pincode || item.pincode}`,
        shippingAddress: `${item.name || item.name}, ${item?.streetAddress || item.street}, ${item.city || item.city}, ${item.state || item.state}, ${item.country || 'Unknown'}, ${item.pincode || item.pincode}`,
        createdAt: item.createdAt || item.created_on,
      }))
    });
    return csv;
  };

  // Handle delete button click to open confirmation modal
  const handleDeleteClick = (hospital) => {
    setHospitalToDelete(hospital);
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (hospitalToDelete) {
      onDelete(hospitalToDelete._id);
      setIsDeleteModalOpen(false);
      setHospitalToDelete(null);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setHospitalToDelete(null);
  };

  return (
    <div className="max-w-8xl mx-auto px-0 py-8">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hospitals</h1>
            <p className="mt-1 text-xs text-gray-500">
              Total hospitals ({hospitals.length})
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/hospitals/add"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
            >
              <Plus size={18} className="mr-2" />
              Add Hospital
            </Link>
          </div>
        </div>

        {/* Search Section */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
          <div className="relative w-full sm:w-96">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search hospitals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
            />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        <div className="min-w-full overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-brand-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hospital name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  State
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Added on
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredHospitals.map((hospital) => (
                <tr
                  key={hospital._id}
                  className="hover:bg-gray-50 transition-colors duration-150"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link
                      to={`/admin/hospitals/${hospital._id}`}
                      className="flex items-center"
                    >
                      <div className="h-8 w-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3">
                        <Users2 size={16} />
                      </div>
                      <span className="text-xs font-medium text-gray-900 hover:text-blue-600">
                        {hospital.name}
                      </span>
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {`${hospital.streetAddress}, ${hospital.city}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {hospital.state}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    {new Date(hospital.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                    <div className="flex items-center space-x-3">
                      <Link
                        to={`/admin/hospitals/edit/${hospital._id}`}
                        className="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                      >
                        <FilePenLine size={18} />
                      </Link>
                      <button
                        onClick={() => handleDeleteClick(hospital)}
                        className="text-red-600 hover:text-red-900 transition-colors duration-150"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for Delete Confirmation */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Confirm Deletion</h2>
              <button
                onClick={handleDeleteCancel}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete the hospital "
              {hospitalToDelete?.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HospitalsTable;